/**
 * Flow editor — binary asset persistence (audio + cover image).
 *
 * Both blobs are stored in the same IndexedDB database so a browser refresh
 * doesn't kill the blob URLs. A single record keyed by `'current'` is enough
 * for each — we only ever cache one track / one cover at a time.
 *
 * DB_VERSION history:
 *   1 — initial (`audio` object store)
 *   2 — added `cover` object store for album art
 */

const DB_NAME = 'synapse-flow-audio';
const DB_VERSION = 2;
const AUDIO_STORE = 'audio';
const COVER_STORE = 'cover';
const KEY = 'current';

interface CachedAudio {
  name: string;
  type: string;
  buffer: ArrayBuffer;
  saved_at: string;
}

interface CachedCover {
  name: string;
  type: string;
  buffer: ArrayBuffer;
  saved_at: string;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = req.result;
      // Version 1 stores
      if (!db.objectStoreNames.contains(AUDIO_STORE)) {
        db.createObjectStore(AUDIO_STORE);
      }
      // Version 2 stores
      if (!db.objectStoreNames.contains(COVER_STORE)) {
        db.createObjectStore(COVER_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// ── Audio ────────────────────────────────────────────────────────────────────

/** Stash the audio file. Quietly no-ops if storage isn't available. */
export async function cacheAudioFile(file: File): Promise<void> {
  try {
    const buffer = await file.arrayBuffer();
    const record: CachedAudio = {
      name: file.name,
      type: file.type || 'audio/mpeg',
      buffer,
      saved_at: new Date().toISOString(),
    };
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(AUDIO_STORE, 'readwrite');
      tx.objectStore(AUDIO_STORE).put(record, KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch {
    /* IndexedDB unavailable or quota exceeded — non-fatal */
  }
}

/**
 * Load the cached audio (if any) and return a fresh blob URL + name.
 * Returns null when no cache exists or when IndexedDB is unavailable.
 */
export async function loadCachedAudio(): Promise<{ url: string; name: string } | null> {
  try {
    const db = await openDb();
    const record = await new Promise<CachedAudio | undefined>((resolve, reject) => {
      const tx = db.transaction(AUDIO_STORE, 'readonly');
      const req = tx.objectStore(AUDIO_STORE).get(KEY);
      req.onsuccess = () => resolve(req.result as CachedAudio | undefined);
      req.onerror = () => reject(req.error);
    });
    db.close();
    if (!record || !record.buffer) return null;
    const blob = new Blob([record.buffer], { type: record.type });
    return { url: URL.createObjectURL(blob), name: record.name };
  } catch {
    return null;
  }
}

/**
 * Read the cached audio as raw bytes — used by the bundle exporter when
 * packing the .flow zip. Returns null when no cache exists.
 */
export async function readCachedAudioBytes(): Promise<{
  bytes: ArrayBuffer;
  name: string;
  type: string;
} | null> {
  try {
    const db = await openDb();
    const record = await new Promise<CachedAudio | undefined>((resolve, reject) => {
      const tx = db.transaction(AUDIO_STORE, 'readonly');
      const req = tx.objectStore(AUDIO_STORE).get(KEY);
      req.onsuccess = () => resolve(req.result as CachedAudio | undefined);
      req.onerror = () => reject(req.error);
    });
    db.close();
    if (!record || !record.buffer) return null;
    return { bytes: record.buffer, name: record.name, type: record.type };
  } catch {
    return null;
  }
}

/** Wipe the cached audio — called when the user explicitly clears. */
export async function clearCachedAudio(): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(AUDIO_STORE, 'readwrite');
      tx.objectStore(AUDIO_STORE).delete(KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch {
    /* ignore */
  }
}

// ── Cover image ──────────────────────────────────────────────────────────────

/** Stash cover image bytes in IndexedDB. Silently no-ops on quota errors. */
export async function cacheCoverImage(
  buffer: ArrayBuffer,
  name: string,
  mime: string,
): Promise<void> {
  try {
    const record: CachedCover = {
      name,
      type: mime || 'image/png',
      buffer,
      saved_at: new Date().toISOString(),
    };
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(COVER_STORE, 'readwrite');
      tx.objectStore(COVER_STORE).put(record, KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch {
    /* non-fatal */
  }
}

/**
 * Load the cached cover and return a fresh blob URL + raw bytes.
 * Returns null when no cover is cached or IndexedDB is unavailable.
 */
export async function loadCachedCover(): Promise<{
  url: string;
  name: string;
  mime: string;
  bytes: ArrayBuffer;
} | null> {
  try {
    const db = await openDb();
    const record = await new Promise<CachedCover | undefined>((resolve, reject) => {
      const tx = db.transaction(COVER_STORE, 'readonly');
      const req = tx.objectStore(COVER_STORE).get(KEY);
      req.onsuccess = () => resolve(req.result as CachedCover | undefined);
      req.onerror = () => reject(req.error);
    });
    db.close();
    if (!record || !record.buffer) return null;
    const blob = new Blob([record.buffer], { type: record.type });
    return {
      url: URL.createObjectURL(blob),
      name: record.name,
      mime: record.type,
      bytes: record.buffer,
    };
  } catch {
    return null;
  }
}

/** Wipe the cached cover — called alongside clearCachedAudio on explicit clear. */
export async function clearCachedCover(): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(COVER_STORE, 'readwrite');
      tx.objectStore(COVER_STORE).delete(KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch {
    /* ignore */
  }
}
